import { useState } from 'react'
import { useQueryClient, useMutation } from '@tanstack/react-query'
import { superadminApi } from '@/api/superadmin'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { X, AlertTriangle } from 'lucide-react'

interface Props {
  tenant: { id: string; name: string }
  onClose: () => void
}

export default function DeleteTenantModal({ tenant, onClose }: Props) {
  const queryClient = useQueryClient()
  const [confirmName, setConfirmName] = useState('')
  const [error, setError] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: () => superadminApi.deleteTenant(tenant.id, confirmName),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['superadmin-tenants'] })
      const { blobsDeleted, blobsFailed } = res.data
      alert(`"${tenant.name}" permanently deleted.\n\nBlobs removed: ${blobsDeleted}${blobsFailed ? ` (${blobsFailed} failed — see logs)` : ''}`)
      onClose()
    },
    onError: (err: any) => {
      setError(err.response?.data?.detail ?? err.response?.data ?? 'Something went wrong.')
    },
  })

  const matches = confirmName.trim() === tenant.name.trim()

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 cursor-pointer"
        >
          <X size={18} />
        </button>

        <div className="flex items-center gap-2.5 mb-4">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-red-50 text-red-600">
            <AlertTriangle size={18} />
          </span>
          <h2 className="text-lg font-semibold text-gray-900">Delete tenant</h2>
        </div>

        <p className="text-sm text-gray-600">
          This permanently deletes <span className="font-semibold text-gray-900">{tenant.name}</span> and
          {' '}<span className="font-medium">all</span> of its data — users, clients, staff, courses, bookings,
          training records, certificates and stored files. <span className="font-semibold text-red-600">This cannot be undone.</span>
        </p>

        <form
          onSubmit={e => { e.preventDefault(); if (matches) { setError(null); mutation.mutate() } }}
          className="mt-5 space-y-4"
        >
          <div className="space-y-1.5">
            <Label htmlFor="confirmName">
              Type <span className="font-mono font-semibold">{tenant.name}</span> to confirm
            </Label>
            <Input
              id="confirmName"
              value={confirmName}
              onChange={e => setConfirmName(e.target.value)}
              placeholder={tenant.name}
              autoComplete="off"
            />
          </div>

          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</p>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button
              type="submit"
              disabled={!matches || mutation.isPending}
              className="bg-red-600 hover:bg-red-700 focus-visible:ring-red-500"
            >
              {mutation.isPending ? 'Deleting…' : 'Delete permanently'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
